if (!customElements.get('related-collections')) {
	class RelatedCollections extends HTMLElement {
		constructor() {
			super();

			this.selectors = {
				sections: 'main .featured-collection',
				sectionHeading: '.section-heading__title',
			};

			this.classes = {
				collectionsItem: 'related-collections__item',
				dropdown: 'related-collections--dropdown',
				isVisible: 'related-collections--is-visible',
				panel: 'related-collections__panel',
				isClosing: 'is-closing',
			};

			this.sections = document.querySelectorAll(this.selectors.sections);
			this.isDropdown = this.classList.contains(this.classes.dropdown);
			this.heading = this.getAttribute('data-heading');
			this.icon = this.getAttribute('data-icon');
			this.scrollOffset = 100;

			this.init();
		}

		init() {
			const isStickyNav = this.dataset.mode === 'sticky-nav';

			if (isStickyNav) {
				this.renderStickyNav();
			} else if (this.isDropdown) {
				this._boundHandleScroll = this.handleScroll.bind(this);
				window.addEventListener('scroll', this._boundHandleScroll, { passive: true });
				this.renderDropdown();
			} else {
				this.renderLinks();
			}
		}

		// -- Shared helpers --

		getNavLinks() {
			try {
				const script = this.querySelector('script[type="application/json"]');
				return script ? JSON.parse(script.textContent) : [];
			} catch (e) {
				return [];
			}
		}

		getSectionTitle(section, overrideTitle) {
			return overrideTitle || section.querySelector(this.selectors.sectionHeading)?.textContent.trim();
		}

		createBadge(blockData) {
			if (!blockData?.label) return null;

			const badge = document.createElement('span');
			badge.classList.add('related-collections__badge');
			badge.textContent = blockData.label;

			if (blockData.label_color) {
				badge.style.backgroundColor = blockData.label_color;
				badge.style.color = blockData.label_text_color;
			}

			return badge;
		}

		scrollToSection(section) {
			const top = section.getBoundingClientRect().top + window.scrollY - this.scrollOffset;
			window.scrollTo({ top, behavior: 'smooth' });
		}

		getTemplateImages() {
			if (!this._parsedImages) {
				const template = this.querySelector('.related-collections__images');

				if (template) {
					const parser = new DOMParser();
					const doc = parser.parseFromString(template.textContent.trim(), 'text/html');
					this._parsedImages = Array.from(doc.querySelectorAll('figure'));
				} else {
					this._parsedImages = [];
				}
			}

			return this._parsedImages;
		}

		// -- Grid mode --

		renderLinks() {
			const fragment = document.createDocumentFragment();

			this.sections.forEach((section, index) => {
				const link = this.createLink(section, index);

				if (link) {
					link.addEventListener('click', (event) => {
						event.preventDefault();
						this.scrollToSection(section);
					});

					fragment.appendChild(link);
				}
			});

			this.appendChild(fragment);
		}

		createLink(section, index, overrideTitle) {
			const title = this.getSectionTitle(section, overrideTitle);
			const sectionId = section.id;

			if (!title || !sectionId) return null;

			const link = document.createElement('a');
			link.classList.add(this.classes.collectionsItem);
			link.href = `#${sectionId}`;

			if (!this.isDropdown) {
				const images = this.getTemplateImages();
				const image = images[index];

				if (image) {
					link.insertAdjacentHTML('afterbegin', image.outerHTML);
				}
			}

			const span = document.createElement('span');
			span.textContent = title;
			link.appendChild(span);

			return link;
		}

		// -- Dropdown mode --

		handleScroll() {
			const headerEl = document.querySelector('.site-header');
			const headerBottom = headerEl ? headerEl.getBoundingClientRect().bottom : 0;

			if (headerBottom < 0) {
				this.classList.add(this.classes.isVisible);
			} else {
				this.classList.remove(this.classes.isVisible);
			}
		}

		renderDropdown() {
			const { classes, sections, icon, heading } = this;
			const navLinks = this.getNavLinks();

			const details = document.createElement('details');

			const summary = document.createElement('summary');
			summary.textContent = heading;

			if (icon) {
				const iconContainer = document.createElement('span');
				iconContainer.classList.add('icon');
				iconContainer.innerHTML = icon;
				summary.appendChild(iconContainer);
			}

			details.appendChild(summary);

			const panel = document.createElement('div');
			panel.classList.add(classes.panel);

			sections.forEach((section, index) => {
				const blockData = navLinks[index] || {};
				const link = this.createLink(section, index, blockData.title);

				if (link) {
					const badge = this.createBadge(blockData);
					if (badge) link.appendChild(badge);

					link.addEventListener('click', (event) => {
						event.preventDefault();
						this.scrollToSection(section);
						this.close(details, panel);
					});

					panel.appendChild(link);
				}
			});

			details.appendChild(panel);
			this.appendChild(details);

			this.applyCollapsibleBehavior(details, summary, panel);
		}

		applyCollapsibleBehavior(details, summary, panel) {
			summary.addEventListener('click', (evt) => {
				evt.preventDefault();
				if (!details.open) {
					this.open(details, panel);
				} else {
					this.close(details, panel);
				}
			});

			details.addEventListener('transitionend', (evt) => {
				if (evt.target !== panel) return;
				if (details.classList.contains(this.classes.isClosing)) {
					details.classList.remove(this.classes.isClosing);
					details.open = false;
				}
				panel.removeAttribute('style');
			});
		}

		open(details, panel) {
			panel.style.height = '0';

			requestAnimationFrame(() => {
				details.open = true;
				panel.style.height = `${panel.scrollHeight}px`;
			});
		}

		close(details, panel) {
			panel.style.height = `${panel.scrollHeight}px`;
			details.classList.add(this.classes.isClosing);

			requestAnimationFrame(() => {
				panel.style.height = '0';
			});
		}

		// -- Sticky nav mode --

		renderStickyNav() {
			const container = this.querySelector('.sticky-nav__inner');
			const navLinks = this.getNavLinks();
			const linkList = document.createElement('ul');
			linkList.classList.add('link-list', 'sticky-nav__list');

			this.sections.forEach((section, index) => {
				const sectionId = section.id;
				if (!sectionId) return;

				const blockData = navLinks[index] || {};
				const title = this.getSectionTitle(section, blockData.title);
				if (!title) return;

				const li = document.createElement('li');
				const link = document.createElement('a');
				link.classList.add('sticky-nav__link');
				link.href = `#${sectionId}`;
				link.textContent = title;

				const badge = this.createBadge(blockData);
				if (badge) link.appendChild(badge);

				link.addEventListener('click', (e) => {
					e.preventDefault();
					this.scrollToSection(section);
				});

				li.appendChild(link);
				linkList.appendChild(li);
			});

			const rightBtn = container.querySelector('.scrollable-navigation-button--right');
			container.insertBefore(linkList, rightBtn);

			this._setupStickySection();
			this._setupHideStickyHeader();
			this._setupScrollableNav(container, linkList);
			this._setupScrollSpy(linkList);
		}

		_setupStickySection() {
			const shopifySection = this.closest('.shopify-section');
			if (shopifySection) {
				shopifySection.style.position = 'sticky';
				shopifySection.style.top = '0';
				shopifySection.style.zIndex = '100';
			}
		}

		_setupHideStickyHeader() {
			const hide = () => {
				const sh = document.querySelector('.sticky-header');
				if (sh) sh.style.display = 'none';
			};

			hide();
			new MutationObserver(hide).observe(document.body, { childList: true });
		}

		_setupScrollableNav(container, linkList) {
			const leftBtn = container.querySelector('.scrollable-navigation-button--left');
			const rightBtn = container.querySelector('.scrollable-navigation-button--right');

			const checkOverflow = () => {
				this.classList.toggle(
					'scrolling-navigation-enabled',
					linkList.scrollWidth > linkList.offsetWidth
				);
			};

			window.addEventListener('resize', debounce(checkOverflow, 200));
			checkOverflow();

			leftBtn?.addEventListener('click', () => {
				linkList.scrollBy({ left: -200, behavior: 'smooth' });
			});
			rightBtn?.addEventListener('click', () => {
				linkList.scrollBy({ left: 200, behavior: 'smooth' });
			});
		}

		_setupScrollSpy(linkList) {
			const links = linkList.querySelectorAll('.sticky-nav__link');
			const sectionEls = Array.from(this.sections);

			const updateActive = () => {
				const scrollY = window.scrollY + this.scrollOffset + 60;
				let currentId = sectionEls[0]?.id;

				sectionEls.forEach((s) => {
					if (s.offsetTop <= scrollY) currentId = s.id;
				});

				links.forEach((l) => {
					const isActive = l.getAttribute('href') === `#${currentId}`;
					l.classList.toggle('sticky-nav__link--active', isActive);

					if (isActive) {
						const linkLeft = l.offsetLeft - linkList.offsetLeft;
						const center = linkLeft - (linkList.offsetWidth / 2) + (l.offsetWidth / 2);
						linkList.scrollTo({ left: center, behavior: 'smooth' });
					}
				});
			};

			window.addEventListener('scroll', updateActive, { passive: true });
			updateActive();
		}
	}

	customElements.define('related-collections', RelatedCollections);
}
